/**
 * Apollo.io API Service
 *
 * Credit-Conscious Design:
 * - Aggressive caching (10 min for company data, 30 min for enrichment)
 * - Batch enrichment requests when possible
 * - Local storage persistence for enriched data
 * - Skip API calls for already-enriched companies
 * - Lazy loading - only fetch when user explicitly requests
 */

import { apolloClient } from './client';
import { logger } from '@/utils/logger';
import type { Visitor } from '@/types';
import type {
  ApolloOrganization,
  ApolloPerson,
  ApolloEnrichmentTransformed,
  ApolloPersonSearchRequest,
  ApolloPersonSearchResponse,
  ApolloOrganizationEnrichResponse,
} from '@/types/api/apollo.types';
import type {
  ApolloCampaign,
  ApolloCampaignStep,
  ApolloRepliedContact,
  ApolloMailboxHealth,
  ApolloSenderStat,
  ApolloPipelineFunnel,
  ApolloUnderperformer,
  ApolloSendTimeHeatmap,
  ApolloReplySentiment,
  ApolloPersonalizedOpenerAB,
  ApolloTemplatePerformance,
  ApolloAccountRollup,
  ApolloIntentSignal,
} from '@/types/api/apollo-campaigns.types';

// ============================================================================
// Local Storage Keys
// ============================================================================

const STORAGE_KEYS = {
  ENRICHED_COMPANIES: 'apollo_enriched_companies',
  VISITORS_CACHE: 'apollo_visitors_cache',
  CONTACTS_CACHE: 'apollo_contacts_cache',
} as const;

// ============================================================================
// Local Storage Helpers (Persist across sessions to save credits)
// ============================================================================

interface StoredEnrichment {
  data: ApolloEnrichmentTransformed;
  timestamp: number;
}

function getStoredEnrichments(): Record<string, StoredEnrichment> {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.ENRICHED_COMPANIES);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

const MAX_CACHED_ENRICHMENTS = 200;

function storeEnrichment(domain: string, data: ApolloEnrichmentTransformed): void {
  try {
    const stored = getStoredEnrichments();
    stored[domain] = {
      data,
      timestamp: Date.now(),
    };

    // Evict oldest entries if cache exceeds limit
    const keys = Object.keys(stored);
    if (keys.length > MAX_CACHED_ENRICHMENTS) {
      const sorted = keys.sort((a, b) => (stored[a]!.timestamp) - (stored[b]!.timestamp));
      const toRemove = sorted.slice(0, keys.length - MAX_CACHED_ENRICHMENTS);
      for (const key of toRemove) {
        delete stored[key];
      }
    }

    localStorage.setItem(STORAGE_KEYS.ENRICHED_COMPANIES, JSON.stringify(stored));
  } catch {
    // Storage full or unavailable - continue without persistence
  }
}

function getStoredEnrichment(domain: string): ApolloEnrichmentTransformed | null {
  const stored = getStoredEnrichments();
  const entry = stored[domain];

  if (!entry) return null;

  // 30 day TTL for stored enrichments (company data rarely changes)
  const thirtyDays = 30 * 24 * 60 * 60 * 1000;
  if (Date.now() - entry.timestamp > thirtyDays) {
    // Expired - remove and return null
    delete stored[domain];
    localStorage.setItem(STORAGE_KEYS.ENRICHED_COMPANIES, JSON.stringify(stored));
    return null;
  }

  return entry.data;
}

// ============================================================================
// Data Transformers
// ============================================================================

function transformOrganizationToEnrichment(
  org: ApolloOrganization,
  contacts: ApolloPerson[] = []
): ApolloEnrichmentTransformed {
  const getEmployeeRange = (count: number | null): string => {
    if (!count) return 'Unknown';
    if (count < 50) return '< 50';
    if (count < 200) return '50-200';
    if (count < 1000) return '200-1,000';
    if (count < 5000) return '1,000-5,000';
    if (count < 10000) return '5,000-10,000';
    return '10,000+';
  };

  return {
    // Basic info
    industry: org.industry ?? org.industries?.[0] ?? 'Unknown',
    secondaryIndustries: org.secondary_industries?.length ? org.secondary_industries : undefined,
    size: getEmployeeRange(org.estimated_num_employees),
    employeeCount: org.estimated_num_employees ?? undefined,
    location: [org.city, org.state, org.country].filter(Boolean).join(', ') || 'Unknown',
    website: org.website_url ?? org.primary_domain ?? '',
    linkedin: org.linkedin_url ?? '',
    twitter: org.twitter_url ?? undefined,
    facebook: org.facebook_url ?? undefined,
    crunchbase: org.crunchbase_url ?? undefined,

    // Company details
    founded: org.founded_year ?? 0,
    revenue: org.annual_revenue_printed ?? 'Unknown',
    annualRevenue: org.annual_revenue ?? undefined,
    shortDescription: org.short_description ?? org.seo_description ?? undefined,
    keywords: org.keywords?.length ? org.keywords : undefined,

    // Funding info
    totalFunding: org.total_funding_printed ?? undefined,
    latestFundingStage: org.latest_funding_stage ?? undefined,
    fundingEvents: org.funding_events?.length
      ? org.funding_events.map((f) => ({
          date: f.date,
          type: f.type,
          amount: f.amount,
          investors: f.investors,
        }))
      : undefined,

    // Tech stack
    technologies: org.technology_names ?? [],

    // Address details
    streetAddress: org.street_address ?? undefined,
    city: org.city ?? undefined,
    state: org.state ?? undefined,
    postalCode: org.postal_code ?? undefined,
    country: org.country ?? undefined,
    phone: org.phone ?? org.primary_phone?.number ?? undefined,

    // Other
    logoUrl: org.logo_url ?? undefined,
    alexaRanking: org.alexa_ranking ?? undefined,
    recentNews: org.seo_description ?? org.short_description ?? '',
    additionalContacts: contacts.slice(0, 5).map((c) => ({
      name: c.name,
      title: c.title ?? '',
      email: c.email ?? '',
    })),
    rawData: org,
  };
}

// ============================================================================
// Apollo API Service
// ============================================================================

export const apolloService = {
  /**
   * Get website visitors from the last N days
   * Uses backend proxy to avoid exposing API key
   * Caches for 10 minutes to reduce API calls
   *
   * @param days - Number of days to look back (default: 60)
   */
  async getVisitors(days = 60): Promise<Visitor[]> {
    const response = await apolloClient.get<{ visitors: Visitor[] }>(
      `/apollo/visitors?days=${days}&limit=50`,
      {
        cacheTTL: 10 * 60 * 1000, // 10 min cache
      }
    );

    return response.visitors ?? [];
  },

  /**
   * Enrich a company by name (primary) or domain (fallback)
   * CREDIT-CONSCIOUS: Checks local storage first, then API cache
   *
   * @param domain - Company domain (e.g., "acme.com") - used as fallback and cache key
   * @param options.companyName - Company name for search (more accurate than domain)
   * @param options.forceRefresh - Skip cache and fetch fresh data (costs 1 credit)
   */
  async enrichCompany(
    domain: string,
    options: { companyName?: string; forceRefresh?: boolean } | boolean = false
  ): Promise<ApolloEnrichmentTransformed | null> {
    // Handle legacy boolean parameter for forceRefresh
    const forceRefresh = typeof options === 'boolean' ? options : (options.forceRefresh ?? false);
    const companyName = typeof options === 'boolean' ? undefined : options.companyName;

    const normalizedDomain = domain.toLowerCase().replace(/^www\./, '');

    // 1. Check local storage first (FREE - no API call)
    if (!forceRefresh) {
      const stored = getStoredEnrichment(normalizedDomain);
      if (stored) {
        logger.log(`[Apollo] Using stored enrichment for ${normalizedDomain}`);
        return stored;
      }
    }

    // 2. Call API (costs credits)
    // If company name is provided, server will search by name first, then fall back to domain
    try {
      const lookupMethod = companyName ? `name: "${companyName}"` : `domain: ${normalizedDomain}`;
      logger.log(`[Apollo] Fetching enrichment by ${lookupMethod} (costs 1 credit)`);

      const response = await apolloClient.post<ApolloOrganizationEnrichResponse>(
        '/apollo/enrich/organization',
        {
          name: companyName, // Primary lookup - by company name
          domain: normalizedDomain, // Fallback - by domain
        },
        {
          cacheTTL: 30 * 60 * 1000, // 30 min API cache
          cacheKey: `enrich:${normalizedDomain}`,
        }
      );

      if (!response.organization) {
        return null;
      }

      const transformed = transformOrganizationToEnrichment(response.organization);

      // Debug: log what Apollo returned vs what we're using
      logger.log('[Apollo] Raw response fields:', {
        name: response.organization.name,
        logo_url: response.organization.logo_url,
        short_description: response.organization.short_description,
        seo_description: response.organization.seo_description,
        founded_year: response.organization.founded_year,
        total_funding_printed: response.organization.total_funding_printed,
        latest_funding_stage: response.organization.latest_funding_stage,
        keywords: response.organization.keywords,
        twitter_url: response.organization.twitter_url,
        facebook_url: response.organization.facebook_url,
        crunchbase_url: response.organization.crunchbase_url,
        phone: response.organization.phone,
      });

      // 3. Store in local storage for future sessions
      storeEnrichment(normalizedDomain, transformed);

      return transformed;
    } catch (error) {
      logger.simpleError(`[Apollo] Enrichment failed for ${normalizedDomain}:`, error);
      return null;
    }
  },

  /**
   * Batch enrich multiple companies
   * CREDIT-CONSCIOUS: Only enriches companies not already in storage
   *
   * @param domains - Array of company domains
   * @returns Map of domain -> enrichment data
   */
  async batchEnrichCompanies(
    domains: string[]
  ): Promise<Map<string, ApolloEnrichmentTransformed>> {
    const results = new Map<string, ApolloEnrichmentTransformed>();
    const domainsToFetch: string[] = [];

    // Filter out already-stored companies
    for (const domain of domains) {
      const normalized = domain.toLowerCase().replace(/^www\./, '');
      const stored = getStoredEnrichment(normalized);

      if (stored) {
        results.set(normalized, stored);
      } else {
        domainsToFetch.push(normalized);
      }
    }

    logger.log(
      `[Apollo] Batch enrich: ${results.size} from cache, ${domainsToFetch.length} to fetch`
    );

    // Fetch remaining in parallel batches (3 concurrent to respect rate limits)
    const CONCURRENCY = 3;
    for (let i = 0; i < domainsToFetch.length; i += CONCURRENCY) {
      const batch = domainsToFetch.slice(i, i + CONCURRENCY);
      const batchResults = await Promise.allSettled(
        batch.map(domain => this.enrichCompany(domain))
      );
      batchResults.forEach((result, idx) => {
        if (result.status === 'fulfilled' && result.value) {
          results.set(batch[idx]!, result.value);
        }
      });
    }

    return results;
  },

  /**
   * Search for contacts at a company
   * CREDIT-CONSCIOUS: Uses conservative limits and caching
   *
   * @param companyDomain - Company domain to search
   * @param options - Search filters (seniority, titles)
   */
  async searchContacts(
    companyDomain: string,
    options: {
      seniorities?: string[];
      titles?: string[];
      limit?: number;
    } = {}
  ): Promise<ApolloPerson[]> {
    const { seniorities, titles, limit = 5 } = options;

    const request: ApolloPersonSearchRequest = {
      q_organization_domains: [companyDomain],
      person_seniorities: seniorities as ApolloPersonSearchRequest['person_seniorities'],
      person_titles: titles,
      per_page: Math.min(limit, 10), // Cap at 10 to save credits
      page: 1,
    };

    try {
      const response = await apolloClient.post<ApolloPersonSearchResponse>(
        '/apollo/people/search',
        request,
        {
          cacheTTL: 15 * 60 * 1000, // 15 min cache for contact searches
          cacheKey: `contacts:${companyDomain}:${JSON.stringify(options)}`,
        }
      );

      return response.people ?? [];
    } catch (error) {
      logger.simpleError(`[Apollo] Contact search failed for ${companyDomain}:`, error);
      return [];
    }
  },

  /**
   * Check if a company is already enriched (no API call)
   */
  isCompanyEnriched(domain: string): boolean {
    const normalized = domain.toLowerCase().replace(/^www\./, '');
    return getStoredEnrichment(normalized) !== null;
  },

  /**
   * Get cached enrichment without API call
   */
  getCachedEnrichment(domain: string): ApolloEnrichmentTransformed | null {
    const normalized = domain.toLowerCase().replace(/^www\./, '');
    return getStoredEnrichment(normalized);
  },

  /**
   * Get count of stored enrichments
   */
  getEnrichmentCacheStats(): { count: number; oldestTimestamp: number | null } {
    const stored = getStoredEnrichments();
    const entries = Object.values(stored);

    return {
      count: entries.length,
      oldestTimestamp: entries.length > 0
        ? Math.min(...entries.map((e) => e.timestamp))
        : null,
    };
  },

  /**
   * Clear all cached enrichments
   */
  clearEnrichmentCache(): void {
    localStorage.removeItem(STORAGE_KEYS.ENRICHED_COMPANIES);
    apolloClient.invalidateCache('enrich:');
  },

  /**
   * Get available API requests (from rate limiter)
   */
  getAvailableCredits(): number {
    return apolloClient.getAvailableRequests();
  },

  // ==========================================================================
  // Campaigns (Sequences) — Outreach-compatible methods
  //
  // These mirror apolloCampaignsService.* method signatures so existing consumers
  // (useApolloCampaigns hook, Outbound page, lists panel) can swap with minimal diff.
  // ==========================================================================

  async getCampaigns(): Promise<ApolloCampaign[]> {
    const response = await apolloClient.get<{ sequences: ApolloCampaign[] }>(
      '/apollo/campaigns?per_page=100'
    );
    return response.sequences || [];
  },

  async getCampaignSteps(campaignId: string | number): Promise<ApolloCampaignStep[]> {
    const response = await apolloClient.get<{ steps: ApolloCampaignStep[] }>(
      `/apollo/campaigns/${campaignId}/steps`
    );
    return response.steps || [];
  },

  async getCampaignContacts(
    campaignId: string | number,
    options: { interestedOnly?: boolean; page?: number; perPage?: number } = {}
  ): Promise<{ prospects: ApolloRepliedContact[]; pagination: unknown }> {
    const params = new URLSearchParams();
    if (options.interestedOnly) params.set('interested_only', 'true');
    if (options.page) params.set('page', String(options.page));
    if (options.perPage) params.set('per_page', String(options.perPage));
    const qs = params.toString();
    return apolloClient.get<{ prospects: ApolloRepliedContact[]; pagination: unknown }>(
      `/apollo/campaigns/${campaignId}/contacts${qs ? `?${qs}` : ''}`
    );
  },

  async getInterestedInCampaign(campaignId: string | number): Promise<ApolloRepliedContact[]> {
    const response = await apolloClient.get<{ prospects: ApolloRepliedContact[] }>(
      `/apollo/campaigns/${campaignId}/interested`
    );
    return response.prospects || [];
  },

  async getAllRepliedProspects(): Promise<ApolloRepliedContact[]> {
    const response = await apolloClient.get<{ prospects: ApolloRepliedContact[] }>(
      '/apollo/replies/all'
    );
    return response.prospects || [];
  },

  /**
   * No-op shim: Apollo uses a server-side API key, no per-user OAuth.
   * Returns "connected" so existing UI status checks pass.
   */
  async getConnectionStatus(): Promise<{ connected: boolean; mode: 'api_key' }> {
    return { connected: true, mode: 'api_key' };
  },

  async getMailboxes(): Promise<Array<{
    id: string;
    email: string;
    userId: string;
    isDefault: boolean;
    provider?: string;
  }>> {
    const response = await apolloClient.get<{ mailboxes: Array<{
      id: string;
      email: string;
      userId: string;
      isDefault: boolean;
      provider?: string;
    }> }>('/apollo/mailboxes');
    return response.mailboxes || [];
  },

  async createContact(payload: {
    firstName?: string;
    lastName?: string;
    email: string;
    title?: string;
    company?: string;
    linkedinUrl?: string;
  }): Promise<{ id: string; email: string; name: string }> {
    const body: Record<string, unknown> = { email: payload.email };
    if (payload.firstName) body.first_name = payload.firstName;
    if (payload.lastName) body.last_name = payload.lastName;
    if (payload.title) body.title = payload.title;
    if (payload.company) body.organization_name = payload.company;
    if (payload.linkedinUrl) body.linkedin_url = payload.linkedinUrl;
    const response = await apolloClient.post<{ prospect: { id: string; email: string; name: string } }>(
      '/apollo/contacts',
      body
    );
    return response.prospect;
  },

  async addContactsToCampaign(
    campaignId: string | number,
    contactIds: string[],
    mailboxId: string
  ): Promise<{ added: number }> {
    const response = await apolloClient.post<{ added: number }>(
      `/apollo/campaigns/${campaignId}/add-contacts`,
      { contact_ids: contactIds, send_email_from_email_account_id: mailboxId }
    );
    return { added: response.added };
  },

  async syncLeadsToCampaign(
    campaignId: string | number,
    mailboxId: string,
    leads: Array<{
      id: string;
      firstName?: string;
      lastName?: string;
      name?: string;
      email: string;
      title?: string;
      company?: string;
      linkedinUrl?: string;
    }>
  ): Promise<{
    success: Array<{ leadId: string; prospectId: string }>;
    failed: Array<{ leadId: string; error: string }>;
  }> {
    return apolloClient.post('/apollo/sync-leads', {
      campaign_id: String(campaignId),
      send_email_from_email_account_id: mailboxId,
      leads,
    });
  },

  // ==========================================================================
  // Marketer-focused endpoints
  // ==========================================================================

  async getMailboxHealth(): Promise<{ mailboxes: ApolloMailboxHealth[]; unhealthyCount: number }> {
    const r = await apolloClient.get<{ mailboxes: ApolloMailboxHealth[]; unhealthyCount: number }>(
      '/apollo/mailboxes/health'
    );
    return { mailboxes: r.mailboxes || [], unhealthyCount: r.unhealthyCount || 0 };
  },

  async getSenderStats(sample = 500): Promise<{ senders: ApolloSenderStat[]; sampleSize: number }> {
    return apolloClient.get(`/apollo/sender-stats?sample=${sample}`);
  },

  async getPipelineFunnel(): Promise<ApolloPipelineFunnel> {
    return apolloClient.get('/apollo/pipeline-funnel');
  },

  async getUnderperformers(): Promise<{ campaigns: ApolloUnderperformer[] }> {
    return apolloClient.get('/apollo/underperformers');
  },

  async getSendTimeHeatmap(sample = 500): Promise<ApolloSendTimeHeatmap> {
    return apolloClient.get(`/apollo/send-time-heatmap?sample=${sample}`);
  },

  async getReplySentiment(sample = 500): Promise<ApolloReplySentiment> {
    return apolloClient.get(`/apollo/reply-sentiment?sample=${sample}`);
  },

  async getPersonalizedOpenerAB(sample = 500): Promise<ApolloPersonalizedOpenerAB> {
    return apolloClient.get(`/apollo/personalized-opener-ab?sample=${sample}`);
  },

  async getTemplatePerformance(): Promise<ApolloTemplatePerformance> {
    return apolloClient.get('/apollo/template-performance');
  },

  async getAccountRollup(page = 1): Promise<{ accounts: ApolloAccountRollup[]; pagination?: unknown }> {
    return apolloClient.get(`/apollo/account-rollup?page=${page}`);
  },

  async getIntentSignals(domain: string): Promise<ApolloIntentSignal> {
    return apolloClient.post('/apollo/enrich/intent', { domain });
  },
};

export default apolloService;
